import { FormControl, FormLabel } from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { FieldPath, PathValue, useFormContext } from 'react-hook-form';

import { IsolatedMultiSelect } from 'packages/common-frontend/components/MultiSelect';
import { StrictFieldValues } from '~/core/routes/settings/implementations/common/Inputs/types';
import {
  Typography,
  TypographyProps,
} from '~/packages/common-frontend/components/Typography';

// This type only allows react-hook-form paths that reference string[] values.
type StringArrayPath<T extends StrictFieldValues> = {
  [K in FieldPath<T>]: PathValue<T, K> extends readonly string[] ? K : never;
}[FieldPath<T>];

type IsolatedMultiSelectInputProps<T extends StrictFieldValues> = {
  counterLabel?: string;
  formLabelStyle?: TypographyProps;
  label?: string;
  name: StringArrayPath<T>;
  options: { label: React.ReactNode; value: string }[];
  optionsHeader?: string;
  placeholder?: string;
  titleText: string;
  isSearchable?: boolean;
};

/**
 * A multi-select dropdown component that opens in an isolated modal for selecting multiple options. Only works with fields typed as string[].
 *
 * @template T - The form data type that extends StrictFieldValues
 *
 * Props:
 * - `name`: Field name in the form. Must reference a field typed as string[]
 * - `label`: Optional label displayed above the multi-select button
 * - `titleText`: Title text shown in the modal header when opened
 * - `options`: Array of options to display in the multi-select dropdown
 * - `placeholder`: Optional placeholder text shown on the button when no options are selected
 * - `counterLabel`: Optional custom label for the selection counter (e.g., "items" shows "3 items selected")
 * - `isSearchable`: Optional boolean for search functionality in dropdown (default: true)
 * - `optionsHeader`: Optional header text displayed above the options in the dropdown
 *
 * @example
 * // Basic multi-select
 * <IsolatedMultiSelectInput<FormData>
 *   name="tags"
 *   label="Tags"
 *   titleText="Select Tags"
 *   options={[
 *     { label: 'ABC Providers', value: 'abc' },
 *     { label: 'Acme Borrowers', value: 'acme' }
 *   ]}
 * />
 */
export const IsolatedMultiSelectInput = <T extends StrictFieldValues>({
  counterLabel,
  formLabelStyle = { fontWeight: 'bold' },
  label,
  name,
  options,
  placeholder = '',
  titleText,
  isSearchable = true,
  optionsHeader,
}: IsolatedMultiSelectInputProps<T>) => {
  const { register, setValue, getValues } = useFormContext<T>();

  useEffect(() => {
    register(name);
  }, [name, register]);

  return (
    <FormControl>
      {label && (
        <FormLabel>
          <Typography {...formLabelStyle}>{label}</Typography>
        </FormLabel>
      )}
      <IsolatedMultiSelect
        autoApplyOptions
        buttonProps={{
          width: ['100%'],
        }}
        counterLabel={counterLabel}
        // can cast to string[] here because of the StringArrayPath type
        initialSelections={(getValues(name) as string[]) || []}
        isSearchable={isSearchable}
        placeholderText={placeholder}
        options={options}
        optionsHeader={optionsHeader}
        onApplyOptions={(selected: string[]) => {
          // casting to any here is ok because StringArrayPath ensures that the value
          // of this form field will be string[]
          setValue(name, selected as any);
        }}
        showSelectedCount
        titleText={titleText}
      />
    </FormControl>
  );
};
